import { Card, CardContent, CardHeader } from "@blueskydataplatform/shared-ui/components/ui/card";
import { Separator } from "@blueskydataplatform/shared-ui/components/ui/separator";
import { Skeleton } from "@blueskydataplatform/shared-ui/components/ui/skeleton";
import { useUseCases } from "../api/get-use-cases";
import PlatformSignUpForm from "./platform-sign-up-form";
import McpSignUpForm from "./mcp-sign-up-form";
import useQueryParams from "@/hooks/use-query-params";
import { normalizeSignUpConsentCheckboxes } from "@blueskydataplatform/shared-ui/utils/sign-up-consent-checkboxes";

const PlatformSignUpFormSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <Card className="w-full max-w-xl">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-2 items-center">
          <Skeleton className="h-14 w-44 rounded-md bg-muted" />
        </div>
        <Skeleton className="mx-auto h-8 w-56 rounded-md bg-muted" />
        <Skeleton className="mx-auto h-4 w-40 rounded-md bg-muted" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-14 bg-muted" />
            <Skeleton className="h-10 w-full rounded-md bg-muted" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 bg-muted" />
            <Skeleton className="h-10 w-full rounded-md bg-muted" />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-64 max-w-full bg-muted" />
            <div className="grid grid-cols-3 gap-2">
              <Skeleton className="h-10 rounded-md bg-muted" />
              <Skeleton className="h-10 rounded-md bg-muted" />
              <Skeleton className="h-10 rounded-md bg-muted" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-36 bg-muted" />
            <div className="grid grid-cols-1 gap-2">
              <Skeleton className="h-10 w-full rounded-md bg-muted" />
              <Skeleton className="h-10 w-full rounded-md bg-muted" />
              <Skeleton className="h-10 w-full rounded-md bg-muted" />
              <Skeleton className="h-10 w-full rounded-md bg-muted" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-20 bg-muted" />
            <Skeleton className="h-10 w-full rounded-md bg-muted" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-52 max-w-full bg-muted" />
            <Skeleton className="h-10 w-full rounded-md bg-muted" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-20 bg-muted" />
            <Skeleton className="h-10 w-full rounded-md bg-muted" />
            <Skeleton className="h-3 w-48 bg-muted" />
          </div>
        </div>

        <div className="flex items-start gap-3 pt-1">
          <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-sm bg-muted" />
          <div className="flex-1 space-y-2 pt-0.5">
            <Skeleton className="h-3 w-full max-w-md bg-muted" />
            <Skeleton className="h-3 w-32 bg-muted" />
          </div>
        </div>

        <Skeleton className="h-10 w-full rounded-md bg-muted" />
        <div className="flex justify-center">
          <Skeleton className="h-4 w-36 bg-muted" />
        </div>
      </CardContent>
    </Card>
  </div>
);

const McpSignUpFormSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
    <Card className="w-full max-w-xl">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-2 items-center">
          <Skeleton className="h-14 w-44 rounded-md bg-muted" />
        </div>
        <Skeleton className="mx-auto h-8 w-64 rounded-md bg-muted" />
        <Skeleton className="mx-auto h-4 w-56 rounded-md bg-muted" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-14 bg-muted" />
          <Skeleton className="h-10 w-full rounded-md bg-muted" />
        </div>
        <div className="flex items-start gap-3 pt-1">
          <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-sm bg-muted" />
          <Skeleton className="h-3 w-64 max-w-full bg-muted" />
        </div>
        <Skeleton className="h-10 w-full rounded-md bg-muted" />
      </CardContent>
    </Card>
  </div>
);

const PlatformSignUpFormWrapper = () => {
  const { userType: userTypeQuery, isFromMCP } = useQueryParams();
  const { data, isLoading: isLoadingUseCases } = useUseCases();
  if (isLoadingUseCases) return isFromMCP ? <McpSignUpFormSkeleton /> : <PlatformSignUpFormSkeleton />;
  const useCases = data?.useCases ?? [];
  const consentCheckboxes = data?.consentCheckboxes ?? normalizeSignUpConsentCheckboxes();

  // The lean form collects no country, so it must never render where a geo restriction
  // applies — the same condition the server enforces before it accepts a lean payload.
  if (isFromMCP && !data?.geoRestriction?.enabled) {
    return <McpSignUpForm consentCheckboxes={consentCheckboxes} />;
  }

  const initialUseCase = useCases.find(useCase => useCase.slug === userTypeQuery) || useCases[0];
  return (
    <PlatformSignUpForm useCases={useCases} initialUseCase={initialUseCase} consentCheckboxes={consentCheckboxes} />
  );
};

export default PlatformSignUpFormWrapper;
